import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import EmailVerification from '../components/EmailVerification';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Validation
    if (!displayName.trim()) {
      return setError('Please enter your name');
    }

    if (!email.trim()) {
      return setError('Please enter your email');
    }

    if (password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    // Clear any previous errors
    setError('');
    setLoading(true);
    
    // Show email verification step BEFORE creating Firebase account
    setShowVerification(true);
    setLoading(false);
  }

  async function handleVerificationSuccess() {
    try {
      setLoading(true);
      setError('');

      // NOW create the Firebase account after email is verified
      //await signup(email, password, displayName);
      
      // Navigate to dashboard on success
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Signup error:', err);
      setError('Failed to create account: ' + err.message);
      setShowVerification(false);
    } finally {
      setLoading(false);
    }
  }

  function handleCancel() {
    setShowVerification(false);
    setError('');
  }

  // If showing verification, render EmailVerification component
  if (showVerification) {
    return (
      <EmailVerification
        email={email}
        onVerificationSuccess={handleVerificationSuccess}
        onCancel={handleCancel}
      />
    );
  }

  // Otherwise, show signup form
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '450px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        padding: '40px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ fontSize: '64px', marginBottom: '10px' }}>🎅</div>
          <h2 style={{ margin: '0 0 10px 0', color: '#C71212', fontSize: '28px' }}>
            Join Secret Santa
          </h2>
          <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
            Create your account to get started
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            marginBottom: '20px',
            padding: '14px',
            backgroundColor: '#fff5f5',
            border: '2px solid #C71212',
            borderRadius: '8px',
            color: '#C71212',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '18px' }}>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* Signup Form */}
        <form onSubmit={handleSubmit}>
          {/* Name Input */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#333',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              placeholder="Enter your name"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '2px solid #ddd',
                borderRadius: '10px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#109433'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
          </div>

          {/* Email Input */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#333',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your.email@example.com"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '2px solid #ddd',
                borderRadius: '10px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#109433'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
          </div>

          {/* Password Input */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#333',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="At least 6 characters"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '2px solid #ddd',
                borderRadius: '10px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#109433'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
            <p style={{
              margin: '6px 0 0 0',
              fontSize: '12px',
              color: '#666'
            }}>
              Must be at least 6 characters
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: loading ? '#ccc' : '#109433',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              marginBottom: '16px'
            }}
            onMouseOver={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#0d7a2a';
              }
            }}
            onMouseOut={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#109433';
              }
            }}
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        {/* Login Link */}
        <p style={{
          textAlign: 'center',
          fontSize: '14px',
          color: '#666',
          margin: '20px 0 0 0'
        }}>
          Already have an account?{' '}
          <a
            href="/login"
            style={{
              color: '#109433',
              textDecoration: 'none',
              fontWeight: '600'
            }}
            onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            Log In
          </a>
        </p>

        {/* Info Box */}
        <div style={{
          marginTop: '24px',
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#495057',
          borderLeft: '4px solid #109433'
        }}>
          <div style={{ marginBottom: '8px' }}>
            <strong>📧 Email Verification Required</strong>
          </div>
          <div>
            We'll send you a verification code to confirm your email address before creating your account.
          </div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '30px',
          textAlign: 'center',
          fontSize: '12px',
          color: '#999'
        }}>
          <p style={{ margin: 0 }}>🎅 Secret Santa</p>
          <p style={{ margin: '5px 0 0 0' }}>Secure Account Creation</p>
        </div>
      </div>
    </div>
  );
}